/**
 * Toast Notification Component
 *
 * Provides user-friendly feedback for API errors, success messages, and info notifications.
 * Supports multiple toast types, auto-dismiss, and manual dismissal.
 */

import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';

// Toast types
export type ToastType = 'success' | 'error' | 'warning' | 'info';

// Toast data structure
export interface ToastData {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number; // milliseconds, 0 for persistent
  action?: {
    label: string;
    onClick: () => void;
  };
}

// Toast context type
interface ToastContextType {
  toasts: ToastData[];
  addToast: (toast: Omit<ToastData, 'id'>) => string;
  removeToast: (id: string) => void;
  clearAll: () => void;
}

// Create context
const ToastContext = createContext<ToastContextType | undefined>(undefined);

// Generate unique ID
const generateId = () => `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Default durations by type
const DEFAULT_DURATIONS: Record<ToastType, number> = {
  success: 3000,
  error: 5000,
  warning: 4000,
  info: 3000,
};

/**
 * Toast Provider Component
 * Wrap your app with this to enable toast notifications
 */
export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastData[]>([]);

  const addToast = useCallback((toast: Omit<ToastData, 'id'>) => {
    const id = generateId();
    const newToast: ToastData = {
      ...toast,
      id,
      duration: toast.duration ?? DEFAULT_DURATIONS[toast.type],
    };

    setToasts((prev) => [...prev, newToast]);
    return id;
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setToasts([]);
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, clearAll }}>
      {children}
      <ToastContainer />
    </ToastContext.Provider>
  );
};

/**
 * Hook to use toast notifications
 */
export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

/**
 * Toast Container - renders all active toasts
 */
const ToastContainer: React.FC = () => {
  const { toasts } = useContext(ToastContext)!;

  return (
    <div
      style={{
        position: 'fixed',
        top: '16px',
        right: '16px',
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        maxWidth: '400px',
        width: '100%',
        pointerEvents: 'none',
      }}
    >
      {toasts.map((toast) => (
        <Toast key={toast.id} {...toast} />
      ))}
    </div>
  );
};

/**
 * Individual Toast Component
 */
const Toast: React.FC<ToastData> = ({ id, type, title, message, duration, action }) => {
  const { removeToast } = useToast();
  const [isExiting, setIsExiting] = useState(false);

  // Auto-dismiss timer
  useEffect(() => {
    if (duration && duration > 0) {
      const timer = setTimeout(() => {
        setIsExiting(true);
        setTimeout(() => removeToast(id), 300); // Wait for exit animation
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [id, duration, removeToast]);

  const handleDismiss = () => {
    setIsExiting(true);
    setTimeout(() => removeToast(id), 300);
  };

  // Icon based on type
  const getIcon = () => {
    switch (type) {
      case 'success':
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              fill="currentColor"
            />
          </svg>
        );
      case 'error':
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              fill="currentColor"
            />
          </svg>
        );
      case 'warning':
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              fill="currentColor"
            />
          </svg>
        );
      case 'info':
      default:
        return (
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              fill="currentColor"
            />
          </svg>
        );
    }
  };

  // Colors based on type
  const getColors = () => {
    switch (type) {
      case 'success':
        return { bg: '#10B981', iconBg: '#059669', text: '#ECFDF5' };
      case 'error':
        return { bg: '#EF4444', iconBg: '#DC2626', text: '#FEF2F2' };
      case 'warning':
        return { bg: '#F59E0B', iconBg: '#D97706', text: '#FFFBEB' };
      case 'info':
      default:
        return { bg: '#3B82F6', iconBg: '#2563EB', text: '#EFF6FF' };
    }
  };

  const colors = getColors();

  return (
    <div
      style={{
        backgroundColor: colors.bg,
        color: colors.text,
        borderRadius: '8px',
        padding: '12px 16px',
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        pointerEvents: 'auto',
        transform: isExiting ? 'translateX(120%)' : 'translateX(0)',
        opacity: isExiting ? 0 : 1,
        transition: 'transform 0.3s ease, opacity 0.3s ease',
        animation: 'slideIn 0.3s ease',
      }}
      role="alert"
    >
      <style>
        {`
          @keyframes slideIn {
            from {
              transform: translateX(120%);
              opacity: 0;
            }
            to {
              transform: translateX(0);
              opacity: 1;
            }
          }
        `}
      </style>

      {/* Icon */}
      <div
        style={{
          backgroundColor: colors.iconBg,
          borderRadius: '50%',
          padding: '4px',
          flexShrink: 0,
        }}
      >
        {getIcon()}
      </div>

      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: '14px' }}>{title}</div>
        {message && (
          <div style={{ fontSize: '13px', opacity: 0.9, marginTop: '4px' }}>{message}</div>
        )}
        {action && (
          <button
            onClick={action.onClick}
            style={{
              marginTop: '8px',
              padding: '4px 12px',
              backgroundColor: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              borderRadius: '4px',
              color: 'inherit',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 500,
            }}
          >
            {action.label}
          </button>
        )}
      </div>

      {/* Dismiss button */}
      <button
        onClick={handleDismiss}
        style={{
          background: 'none',
          border: 'none',
          color: 'inherit',
          cursor: 'pointer',
          opacity: 0.7,
          padding: '4px',
          flexShrink: 0,
        }}
        aria-label="Dismiss"
      >
        <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>
    </div>
  );
};

/**
 * Helper functions for common toast types
 */
export const toast = {
  success: (title: string, message?: string) => {
    // This will be implemented when context is available
    console.log('Toast success:', title, message);
  },
  error: (title: string, message?: string) => {
    console.log('Toast error:', title, message);
  },
  warning: (title: string, message?: string) => {
    console.log('Toast warning:', title, message);
  },
  info: (title: string, message?: string) => {
    console.log('Toast info:', title, message);
  },
};

/**
 * Map API error codes to user-friendly messages
 */
export const getErrorMessage = (errorCode: string): { title: string; message: string } => {
  const errorMessages: Record<string, { title: string; message: string }> = {
    VALIDATION_ERROR: {
      title: 'Invalid Input',
      message: 'Please check your input and try again.',
    },
    INVALID_QUERY: {
      title: 'Invalid Search',
      message: 'Please enter a valid search query.',
    },
    QUERY_TOO_SHORT: {
      title: 'Query Too Short',
      message: 'Please enter at least 2 characters.',
    },
    AUTH_ERROR: {
      title: 'Authentication Required',
      message: 'Please log in to continue.',
    },
    EXPIRED_TOKEN: {
      title: 'Session Expired',
      message: 'Please log in again to continue.',
    },
    RATE_LIMIT_EXCEEDED: {
      title: 'Too Many Requests',
      message: 'Please wait a moment and try again.',
    },
    EXTERNAL_API_ERROR: {
      title: 'Service Temporarily Unavailable',
      message: 'Some results may be unavailable. Please try again.',
    },
    CIRCUIT_BREAKER_OPEN: {
      title: 'Service Recovering',
      message: 'Some features are temporarily limited. Please try again shortly.',
    },
    SERVICE_UNAVAILABLE: {
      title: 'Service Unavailable',
      message: 'We are experiencing issues. Please try again later.',
    },
    NO_RESULTS: {
      title: 'No Results Found',
      message: 'Try different search terms or filters.',
    },
    SEARCH_ERROR: {
      title: 'Search Failed',
      message: 'An error occurred while searching. Please try again.',
    },
    INTERNAL_ERROR: {
      title: 'Something Went Wrong',
      message: 'An unexpected error occurred. Please try again.',
    },
  };

  return (
    errorMessages[errorCode] || {
      title: 'Error',
      message: 'An unexpected error occurred. Please try again.',
    }
  );
};

export default Toast;
